import type { ReactNode, VoidFunctionComponent } from 'react';

import { splitArray, wrapWithDiv } from '../../utils';

import Tooltip from './Tooltip';

const getTooltipText = (tooltipItems: any[], tooltipMoreCount: number) => [
  ...tooltipItems.map(wrapWithDiv),
  <span key="tooltipMoreCount">{tooltipMoreCount > 0 && `+ ${tooltipMoreCount} More`}</span>,
];

const isNonEmptyString = (str: unknown) => str !== '';

const listDefault: Array<string | ReactNode> = [];

type Props = {
  list: Array<string | ReactNode>;
  total: number;
  listShownCount?: number;
  tooltipShownCount?: number;
};

/**
 * Displays the first nth items in their own div,
 * and displays the rest in a tooltip with "+ nth More"
 *
 * @param {Props} props - FirstNthAndMore props
 * @param {string[]} props.list - The list of items which should be displayed, first in divs and then in the tooltip.
 * @param {number} props.total - The TOTAL number of items, beyond just the ones being displayed.
 * @param {number} props.listShownCount - Items to be shown in div.
 * @param {number} props.tooltipShownCount - Items to be shown in Tooltip.
 */
const FirstNthAndMore: VoidFunctionComponent<Props> = ({
  list = listDefault,
  total = 0,
  listShownCount = 1,
  tooltipShownCount = 4,
}) => {
  if (total === 0) {
    return null;
  }
  const nonEmptyList: unknown[] = list.filter(isNonEmptyString);
  const nonEmptyTotal: number = total - (list.length - nonEmptyList.length);
  //  Remove any empty strings from the list.
  const [first, rest] = splitArray(nonEmptyList, listShownCount);
  const tooltipItems = rest.slice(0, tooltipShownCount);
  const tooltipMoreCount = nonEmptyTotal - listShownCount - tooltipShownCount;
  return (
    <>
      {first.map(wrapWithDiv)}
      {nonEmptyTotal > listShownCount && (
        <Tooltip tooltip={getTooltipText(tooltipItems, tooltipMoreCount)}>
          <div style={{ display: 'inline-block' }}>
            + {nonEmptyTotal - first.length} More
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default FirstNthAndMore;
